import { useCallback, useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 3000;
const POLL_DURATION_MS = 60_000;

/**
 * Calls `onTick` every 3 seconds for a minute once started, while
 * `isWaiting` holds: it stops as soon as the expected change is in.
 */
export function usePolling(isWaiting: boolean, onTick: () => void) {
  const [pollUntil, setPollUntil] = useState<number | null>(null);
  // The latest callback, without restarting the interval for a new one.
  const onTickRef = useRef(onTick);

  useEffect(() => {
    onTickRef.current = onTick;
  });

  const startPolling = useCallback(() => {
    setPollUntil(Date.now() + POLL_DURATION_MS);
  }, []);

  const stopPolling = useCallback(() => setPollUntil(null), []);

  useEffect(() => {
    if (pollUntil === null || !isWaiting) return;

    const intervalId = window.setInterval(() => {
      if (Date.now() > pollUntil) {
        setPollUntil(null);

        return;
      }

      onTickRef.current();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [pollUntil, isWaiting]);

  return {
    isPolling: pollUntil !== null && isWaiting,
    startPolling,
    stopPolling,
  };
}
